export type AttendanceStatusKey =
  | 'present'
  | 'absent'
  | 'late'
  | 'leave'
  | 'bunk'
  | 'unmark'
  | 'checkout'
  | 'earlyCheckout'
  | 'holiday'
  | 'halfDay'
  | 'excused'
  | 'other'

export type AttendanceStatusTheme = {
  key: AttendanceStatusKey
  label: string
  color: string
  softBg: string
}

const STATUS_MAP: Array<{pattern: RegExp; key: AttendanceStatusKey; label: string}> = [
  {pattern: /^present$/i, key: 'present', label: 'Present'},
  {pattern: /^absent|not\s*present$/i, key: 'absent', label: 'Absent'},
  {pattern: /^late|tardy$/i, key: 'late', label: 'Late'},
  {pattern: /^leave|on\s*leave$/i, key: 'leave', label: 'Leave'},
  {pattern: /^bunk$/i, key: 'bunk', label: 'Bunk'},
  {pattern: /^unmark$/i, key: 'unmark', label: 'Unmark'},
  {pattern: /^checkout$/i, key: 'checkout', label: 'Checkout'},
  {pattern: /^early\s*checkout$/i, key: 'earlyCheckout', label: 'Early Checkout'},
  {pattern: /^holiday$/i, key: 'holiday', label: 'Holiday'},
  {pattern: /^half\s*day$/i, key: 'halfDay', label: 'Half Day'},
  {pattern: /^excused$/i, key: 'excused', label: 'Excused'},
]

export function normalizeStatusKey(raw: string): AttendanceStatusKey {
  const trimmed = (raw || '').trim()
  for (const entry of STATUS_MAP) {
    if (entry.pattern.test(trimmed)) return entry.key
  }
  return 'other'
}

export function statusLabel(raw: string): string {
  const trimmed = (raw || '').trim()
  for (const entry of STATUS_MAP) {
    if (entry.pattern.test(trimmed)) return entry.label
  }
  if (!trimmed) return 'Other'
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

function readCssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

const FALLBACK_COLORS: Record<AttendanceStatusKey, {color: string; softBg: string}> = {
  present: {color: '#50cd89', softBg: 'rgba(80, 205, 137, 0.18)'},
  absent: {color: '#f1416c', softBg: 'rgba(241, 65, 108, 0.18)'},
  late: {color: '#7239ea', softBg: 'rgba(114, 57, 234, 0.18)'},
  leave: {color: '#009ef7', softBg: 'rgba(0, 158, 247, 0.18)'},
  bunk: {color: '#ff6b35', softBg: 'rgba(255, 107, 53, 0.18)'},
  unmark: {color: '#b5b9c8', softBg: 'rgba(161, 165, 183, 0.28)'},
  checkout: {color: '#17c653', softBg: 'rgba(23, 198, 83, 0.18)'},
  earlyCheckout: {color: '#ffc700', softBg: 'rgba(255, 199, 0, 0.22)'},
  holiday: {color: '#7e8299', softBg: 'rgba(126, 130, 153, 0.18)'},
  halfDay: {color: '#ffc700', softBg: 'rgba(255, 199, 0, 0.22)'},
  excused: {color: '#17c653', softBg: 'rgba(23, 198, 83, 0.18)'},
  other: {color: '#a1a5b7', softBg: 'rgba(161, 165, 183, 0.18)'},
}

const CSS_VAR_BY_KEY: Record<AttendanceStatusKey, string> = {
  present: '--bs-success',
  absent: '--bs-danger',
  late: '--bs-info',
  leave: '--bs-primary',
  bunk: '--bs-warning',
  unmark: '--bs-gray-500',
  checkout: '--bs-success',
  earlyCheckout: '--bs-warning',
  holiday: '--bs-secondary',
  halfDay: '--bs-warning',
  excused: '--bs-success',
  other: '--bs-gray-500',
}

export function getStatusTheme(raw: string, theme: 'light' | 'dark'): AttendanceStatusTheme {
  const key = normalizeStatusKey(raw)
  const label = statusLabel(raw)
  const cssVar = CSS_VAR_BY_KEY[key]
  const fallback = FALLBACK_COLORS[key]
  const color = readCssVar(cssVar, fallback.color)
  const softBg =
    theme === 'dark'
      ? key === 'unmark' || key === 'other' || key === 'holiday'
        ? fallback.softBg
        : `${color}33`
      : fallback.softBg

  return {key, label, color, softBg}
}

export const STATUS_RING_ORDER: AttendanceStatusKey[] = [
  'present',
  'late',
  'bunk',
  'halfDay',
  'excused',
  'leave',
  'checkout',
  'earlyCheckout',
  'holiday',
  'absent',
  'unmark',
  'other',
]
